<template>
  <div v-if="error" class="flex flex-row gap-2">
    <div class="text-xs text-error-700">Could not verify that you are human. ({{ error }})</div>
    <div class="text-semibold text-brand-700 text-xs hover:cursor-pointer" @click="reset()">Retry</div>
  </div>
  <div v-if="!isChallengeVisible && !isComplete && !error" class="flex flex-row items-center gap-2">
    <div class="text-xs">Verifying that you are human.</div>
    <LayupSpinner class="text-brand-700" :size="4" />
  </div>
  <div data-testid="turnstile-widget" class="flex justify-center" :class="{ hidden: !isChallengeVisible || error }">
    <div ref="turnstileContainer" data-testid="turnstile-container"></div>
  </div>
</template>

<script lang="ts" setup>
import { CLOUDFLARE_SITE_KEY } from '@/config';
import * as logger from '@/utils/monitoring.utils';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import LayupSpinner from './LayupSpinner.vue';

declare global {
  interface Window {
    turnstile: any;
  }
}

const error = ref('');
const isComplete = ref(false);
const isChallengeVisible = ref(false);

const emit = defineEmits(['success']);

const onSuccess = (token: string) => {
  isComplete.value = true;
  error.value = '';
  emit('success', token);
};

const onExpired = () => {
  isComplete.value = false;
  emit('success', '');
  if (typeof window !== 'undefined' && window.turnstile && widgetId) {
    window.turnstile.reset(widgetId);
  }
};

const onError = (errorCode: string) => {
  error.value = errorCode;
  if (errorCode.startsWith('300') || errorCode.startsWith('600')) {
    // Error code family 300*** and 600*** (e.g. "600010: The generic_challenge_failure")
    // is a response generated by the Turnstile in situations where a potential bot or unusual visitor behavior is detected.
    return;
  }
  if (
    errorCode.startsWith('102') ||
    errorCode.startsWith('103') ||
    errorCode.startsWith('104') ||
    errorCode.startsWith('105') ||
    errorCode.startsWith('106')
  ) {
    // Invalid Parameters: The visitor sent an invalid parameter as part of the challenge towards Turnstile.
    return;
  }
  logger.createWarning(`Turnstile Widget Error: ${errorCode}`);
};

const turnstileContainer = ref<HTMLDivElement | null>(null);
let widgetId: string | undefined;

onMounted(() => {
  let attempts = 0;
  const maxAttempts = 50; // 5 seconds max wait time
  const scriptCheck = () => {
    // Check if Turnstile script is loaded
    if (typeof window === 'undefined' || !window.turnstile) {
      attempts++;
      if (attempts < maxAttempts) {
        // Script not loaded yet, retry after a short delay
        setTimeout(scriptCheck, 100);
        return;
      } else {
        // Give up after max attempts
        error.value = 'Failed to load verification widget';
        logger.createWarning('Turnstile script failed to load after 5 seconds');
        return;
      }
    }
    try {
      widgetId = window.turnstile.render(turnstileContainer.value, {
        sitekey: CLOUDFLARE_SITE_KEY,
        theme: 'light',
        appearance: 'interaction-only',
        callback: (token: string) => {
          onSuccess(token);
        },
        'error-callback': (errorCode: string) => {
          onError(errorCode);
        },
        'before-interactive-callback': () => {
          isChallengeVisible.value = true;
        },
        'after-interactive-callback': () => {
          isChallengeVisible.value = false;
        },
        'expired-callback': onExpired
      });
    } catch (error) {
      logger.createWarning(error);
    }
  };

  scriptCheck();
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined' && window.turnstile && widgetId) {
    window.turnstile.remove(widgetId);
  }
});

const reset = () => {
  error.value = '';
  isComplete.value = false;
  isChallengeVisible.value = false;

  if (typeof window !== 'undefined' && window.turnstile && widgetId) {
    window.turnstile.reset(widgetId);
  }
};

defineExpose({
  reset,
});
</script>
